/**
 * Aspect — First-person walk controls (WASD + mouse look)
 * --------------------------------------------------------------
 * Pointer-lock FPS navigation inside the loaded room. Movement is clamped to
 * floor bounds at eye height. While furniture is selected the player cannot
 * walk; mouse look still works and the piece follows the crosshair.
 */

import * as pc from "playcanvas";
import { isTouchUi } from "../device_profile.js";

const EYE_HEIGHT = 1.6;
const MOVE_SPEED = 3.2;
const SPRINT_MULT = 1.75;
const LOOK_SENS = 0.12;
const PITCH_LIMIT = 85;
const PLAYER_RADIUS = 0.28;

// Touch tuning: left stick = walk, right stick = continuous look.
const TOUCH_JOY_RADIUS = 64;
const TOUCH_LOOK_RADIUS = 64;
const TOUCH_JOY_DEADZONE = 6;
// Phones: calm walk speed + gentler look than desktop WASD / mouse.
const TOUCH_MOVE_SPEED = 1.25;
const TOUCH_LOOK_YAW_RATE = 72;
const TOUCH_LOOK_PITCH_RATE = 48;
const GYRO_YAW_SENS = 0.55;
const GYRO_PITCH_SENS = 0.5;

// Real walk feel — soft accel + clear step bob (not a slide, not a jog).
const WALK_ACCEL = 6.0;
const WALK_ACCEL_TOUCH = 4.5;
/** Meters per full left→right gait cycle (~natural stroll at touch speed). */
const BOB_STRIDE_M = 1.45;
const BOB_HEIGHT = 0.026;
const BOB_SWAY = 0.012;
const BOB_ROLL = 0.48; // degrees
const BOB_PITCH = 0.22; // degrees

function prefersTouchWalk() {
  return isTouchUi();
}

function screenAngle() {
  if (typeof screen !== "undefined" && screen.orientation?.angle != null) return screen.orientation.angle;
  if (typeof window !== "undefined" && window.orientation != null) return Number(window.orientation);
  return 0;
}

function shortestDeltaDeg(from, to) {
  let d = to - from;
  while (d > 180) d -= 360;
  while (d < -180) d += 360;
  return d;
}

export class FirstPersonControls {
  /**
   * @param {pc.AppBase} app
   * @param {pc.Entity} camera
   * @param {import('../scene_manager.js').SceneManager} sceneManager
   * @param {{ gizmo?: import('./gizmo_controls.js').GizmoControls, canvas?: HTMLCanvasElement }} [opts]
   */
  constructor(app, camera, sceneManager, opts = {}) {
    this.app = app;
    this.camera = camera;
    this.scene = sceneManager;
    this.gizmo = opts.gizmo ?? null;
    this.placement = opts.placement ?? null;
    this.canvas = opts.canvas ?? app.graphicsDevice?.canvas ?? null;
    this.touchWalk = prefersTouchWalk();

    this.yaw = 0;
    this.pitch = 0;
    this.locked = false;
    this.enabled = true; // suspended while the scene-view (overhead) camera is active
    /** While set ({x,y,z}), the camera is pinned here (sitting/laying) —
     *  mouse look stays live, WASD is ignored. Managed by SitSystem. */
    this.seatPose = null;

    this._onPointerLockChange = () => {
      this.locked = document.pointerLockElement === this.canvas;
      this.app.fire("fp:lock-changed", { locked: this.locked });
    };
    this._onMouseMove = (e) => this._handleMouseMove(e);
    this._onCanvasClick = () => {
      if (this.placement?.active) return;
      this._requestLock();
    };
    this._onContextMenu = (e) => e.preventDefault();

    // Touch: left stick (walk) + right stick (continuous look) — both hold-to-deflect.
    this._joy = { id: null, baseX: 0, baseY: 0, dx: 0, dy: 0 };
    this._lookTouch = { id: null, baseX: 0, baseY: 0, dx: 0, dy: 0 };

    // Gyroscope look (phones): tilt / turn the device to rotate the camera.
    this.gyroEnabled = false;
    this._gyroLast = null;
    this._gyroPendingYaw = 0;
    this._gyroPendingPitch = 0;
    this._gyroToggling = false;
    this._onDeviceOrientation = (e) => this._handleGyro(e);

    // Walk feel state (smoothed velocity + head bob / sway).
    this._velF = 0;
    this._velS = 0;
    this._bobPhase = 0;
    this._bobAmt = 0;
    this._bobY = 0;
    this._bobSway = 0;
    this._bobRoll = 0;
    this._bobPitch = 0;

    this._bind();
    this._preservePose = null;
    this.app.on("scene:preserve-camera-on-reload", () => this._capturePose());
    this.app.on("scene:room-loaded", (room) => {
      if (this._preservePose) this._restorePose(room);
      else this.spawnInRoom(room);
    });
    this.app.on("update", (dt) => this._update(dt));
    this.app.on("ui:orientation", () => {
      // Drop active sticks after rotate — finger coords are stale in the new layout.
      if (this._joy.id !== null) {
        this._joy.id = null;
        this._joy.dx = 0;
        this._joy.dy = 0;
        this._fireJoystick();
      }
      if (this._lookTouch.id !== null) {
        this._lookTouch.id = null;
        this._lookTouch.dx = 0;
        this._lookTouch.dy = 0;
        this._fireLookStick();
      }
      this._gyroLast = null; // recalibrate gyro against new screen angle
      this._gyroPendingYaw = 0;
      this._gyroPendingPitch = 0;
    });
    this.app.on("fp:gyro-toggle", () => {
      if (this._gyroToggling) return;
      this._gyroToggling = true;
      void this.toggleGyro().finally(() => {
        this._gyroToggling = false;
      });
    });
  }

  _capturePose() {
    const pos = this.camera.getLocalPosition();
    this._preservePose = {
      x: pos.x,
      y: pos.y,
      z: pos.z,
      yaw: this.yaw,
      pitch: this.pitch,
    };
  }

  /** Put the player back where they were before a plan-editor Apply rebuild. */
  _restorePose(room) {
    const saved = this._preservePose;
    this._preservePose = null;
    if (!saved) {
      this.spawnInRoom(room);
      return;
    }
    let x = saved.x;
    let z = saved.z;
    const bounds = room?.bounds;
    if (bounds) {
      x = pc.math.clamp(x, bounds.min[0] + PLAYER_RADIUS, bounds.max[0] - PLAYER_RADIUS);
      z = pc.math.clamp(z, bounds.min[2] + PLAYER_RADIUS, bounds.max[2] - PLAYER_RADIUS);
    }
    const floorY = room?.floorY ?? 0;
    const eyeY = floorY + EYE_HEIGHT;
    this.camera.setLocalPosition(x, eyeY, z);
    this.yaw = saved.yaw;
    this.pitch = pc.math.clamp(saved.pitch, -PITCH_LIMIT, PITCH_LIMIT);
    this._resetWalkFeel();
    this._applyRotation();
  }

  _bind() {
    if (this.canvas) {
      this.canvas.addEventListener("click", this._onCanvasClick);
      this.canvas.addEventListener("contextmenu", this._onContextMenu);
    }
    document.addEventListener("pointerlockchange", this._onPointerLockChange);
    document.addEventListener("mousemove", this._onMouseMove);
    this.app.on("fp:release-lock", () => this.releaseLock());

    // Native document touches: PlayCanvas only sees touches that start on the
    // canvas, so a second finger that lands on HUD chrome never becomes look.
    // Document-level listeners keep move+look working together on phones.
    if (this.touchWalk) {
      this._onNativeStart = (ev) => this._nativeTouchStart(ev);
      this._onNativeMove = (ev) => this._nativeTouchMove(ev);
      this._onNativeEnd = (ev) => this._nativeTouchEnd(ev);
      const opts = { passive: false, capture: true };
      document.addEventListener("touchstart", this._onNativeStart, opts);
      document.addEventListener("touchmove", this._onNativeMove, opts);
      document.addEventListener("touchend", this._onNativeEnd, opts);
      document.addEventListener("touchcancel", this._onNativeEnd, opts);
    } else {
      const touch = this.app.touch;
      if (touch) {
        touch.on(pc.EVENT_TOUCHSTART, this._onTouchStart, this);
        touch.on(pc.EVENT_TOUCHMOVE, this._onTouchMove, this);
        touch.on(pc.EVENT_TOUCHEND, this._onTouchEnd, this);
        touch.on(pc.EVENT_TOUCHCANCEL, this._onTouchEnd, this);
      }
    }
  }

  _fireJoystick() {
    const j = this._joy;
    this.app.fire("fp:joystick", {
      active: j.id !== null,
      x: j.baseX,
      y: j.baseY,
      dx: j.dx,
      dy: j.dy,
      radius: TOUCH_JOY_RADIUS,
    });
  }

  _fireLookStick() {
    const l = this._lookTouch;
    this.app.fire("fp:lookstick", {
      active: l.id !== null,
      x: l.baseX,
      y: l.baseY,
      dx: l.dx,
      dy: l.dy,
      radius: TOUCH_LOOK_RADIUS,
    });
  }

  _setLookStick(id, x, y) {
    this._lookTouch.id = id;
    this._lookTouch.baseX = x;
    this._lookTouch.baseY = y;
    this._lookTouch.dx = 0;
    this._lookTouch.dy = 0;
    this._fireLookStick();
  }

  _updateLookStick(x, y) {
    let dx = x - this._lookTouch.baseX;
    let dy = y - this._lookTouch.baseY;
    const len = Math.hypot(dx, dy);
    if (len > TOUCH_LOOK_RADIUS) {
      dx = (dx / len) * TOUCH_LOOK_RADIUS;
      dy = (dy / len) * TOUCH_LOOK_RADIUS;
    }
    this._lookTouch.dx = dx;
    this._lookTouch.dy = dy;
    this._fireLookStick();
    this._gyroLast = null; // finger look wins; recalibrate gyro after
    this._gyroPendingYaw = 0;
    this._gyroPendingPitch = 0;
  }

  _clearLookStick() {
    if (this._lookTouch.id === null && this._lookTouch.dx === 0 && this._lookTouch.dy === 0) return;
    this._lookTouch.id = null;
    this._lookTouch.dx = 0;
    this._lookTouch.dy = 0;
    this._fireLookStick();
    this._gyroLast = null;
    this._gyroPendingYaw = 0;
    this._gyroPendingPitch = 0;
  }

  /** Hold look stick → continuous yaw/pitch every frame (controller-style). */
  _applyLookStick(dt) {
    if (this._lookTouch.id === null) return;
    const mag = Math.hypot(this._lookTouch.dx, this._lookTouch.dy);
    if (mag < TOUCH_JOY_DEADZONE) return;
    const nx = this._lookTouch.dx / TOUCH_LOOK_RADIUS;
    const ny = this._lookTouch.dy / TOUCH_LOOK_RADIUS;
    this.yaw -= nx * TOUCH_LOOK_YAW_RATE * dt;
    this.pitch = pc.math.clamp(
      this.pitch - ny * TOUCH_LOOK_PITCH_RATE * dt,
      -PITCH_LIMIT,
      PITCH_LIMIT,
    );
    this._applyRotation();
  }

  _touchPoint(t) {
    const rect = this.canvas?.getBoundingClientRect?.();
    const x = t.clientX - (rect?.left ?? 0);
    const y = t.clientY - (rect?.top ?? 0);
    return { id: t.identifier, x, y };
  }

  _uiBlocksTouch(target) {
    if (!(target instanceof Element)) return false;
    if (document.body.classList.contains("buy-soon-open")) return true;
    // Allow walk/look through the scene; only block real interactive chrome.
    return Boolean(
      target.closest(
        "button, a, input, textarea, select, [data-overlay='catalog-panel'], [data-overlay='hover-buy'], [data-overlay='buy-soon'], [data-overlay='touch-plan-bar'], .prompt-bar, .hud-btn, .mode-segment, .provider-picker, .plan-editor, .hover-panel__buy-btn, .buy-soon",
      ),
    );
  }

  _assignTouch(id, x, y) {
    // Lying / sitting: any drag looks around (walk stick is locked anyway).
    if (this.seatPose) {
      if (this._lookTouch.id === null) this._setLookStick(id, x, y);
      return;
    }
    const w = this.canvas?.clientWidth ?? window.innerWidth;
    // Second finger while walking is ALWAYS look — don't require right-half.
    if (this._joy.id !== null && this._lookTouch.id === null && id !== this._joy.id) {
      this._setLookStick(id, x, y);
      return;
    }
    // Second finger while looking: left half becomes move stick.
    if (this._lookTouch.id !== null && this._joy.id === null && id !== this._lookTouch.id && x < w * 0.55) {
      this._joy.id = id;
      this._joy.baseX = x;
      this._joy.baseY = y;
      this._joy.dx = 0;
      this._joy.dy = 0;
      this._fireJoystick();
      return;
    }
    if (x < w * 0.5 && this._joy.id === null) {
      this._joy.id = id;
      this._joy.baseX = x;
      this._joy.baseY = y;
      this._joy.dx = 0;
      this._joy.dy = 0;
      this._fireJoystick();
    } else if (this._lookTouch.id === null) {
      this._setLookStick(id, x, y);
    }
  }

  _nativeTouchStart(ev) {
    if (!this.enabled) return;
    if (this._uiBlocksTouch(ev.target)) return;
    for (const t of ev.changedTouches ?? []) {
      const p = this._touchPoint(t);
      this._assignTouch(p.id, p.x, p.y);
    }
    if (this._joy.id !== null || this._lookTouch.id !== null) ev.preventDefault();
  }

  _nativeTouchMove(ev) {
    if (!this.enabled) return;
    let used = false;
    for (const t of ev.changedTouches ?? []) {
      const p = this._touchPoint(t);
      if (p.id === this._joy.id) {
        let dx = p.x - this._joy.baseX;
        let dy = p.y - this._joy.baseY;
        const len = Math.hypot(dx, dy);
        if (len > TOUCH_JOY_RADIUS) {
          dx = (dx / len) * TOUCH_JOY_RADIUS;
          dy = (dy / len) * TOUCH_JOY_RADIUS;
        }
        this._joy.dx = dx;
        this._joy.dy = dy;
        this._fireJoystick();
        used = true;
      } else if (p.id === this._lookTouch.id) {
        this._updateLookStick(p.x, p.y);
        used = true;
      }
    }
    if (used) ev.preventDefault();
  }

  _nativeTouchEnd(ev) {
    for (const t of ev.changedTouches ?? []) {
      const id = t.identifier;
      if (id === this._joy.id) {
        this._joy.id = null;
        this._joy.dx = 0;
        this._joy.dy = 0;
        this._fireJoystick();
      } else if (id === this._lookTouch.id) {
        this._clearLookStick();
      }
    }
  }

  _onTouchStart(e) {
    if (!this.enabled) return;
    for (const t of e.changedTouches ?? []) {
      this._assignTouch(t.id, t.x, t.y);
    }
    e.event?.preventDefault?.();
  }

  _onTouchMove(e) {
    if (!this.enabled) return;
    for (const t of e.changedTouches ?? []) {
      if (t.id === this._joy.id) {
        let dx = t.x - this._joy.baseX;
        let dy = t.y - this._joy.baseY;
        const len = Math.hypot(dx, dy);
        if (len > TOUCH_JOY_RADIUS) {
          dx = (dx / len) * TOUCH_JOY_RADIUS;
          dy = (dy / len) * TOUCH_JOY_RADIUS;
        }
        this._joy.dx = dx;
        this._joy.dy = dy;
        this._fireJoystick();
      } else if (t.id === this._lookTouch.id) {
        this._updateLookStick(t.x, t.y);
      }
    }
    e.event?.preventDefault?.();
  }

  _onTouchEnd(e) {
    for (const t of e.changedTouches ?? []) {
      if (t.id === this._joy.id) {
        this._joy.id = null;
        this._joy.dx = 0;
        this._joy.dy = 0;
        this._fireJoystick();
      } else if (t.id === this._lookTouch.id) {
        this._clearLookStick();
      }
    }
  }

  _requestLock() {
    // Phones/tablets use the on-screen joystick — pointer lock is desktop-only
    // and often fails or confuses the browser on mobile.
    if (this.touchWalk) return;
    if (!this.enabled || !this.canvas || this.locked) return;
    this.canvas.requestPointerLock?.();
  }

  /**
   * Enable/disable device-gyroscope look. iOS requires a user gesture for
   * DeviceOrientationEvent.requestPermission(). Cross-origin iframes may
   * throw instead of prompting — still attach the listener so delegated
   * gyroscope (iframe allow + Permissions-Policy) can deliver events.
   * @returns {Promise<boolean>}
   */
  async toggleGyro() {
    if (this.gyroEnabled) {
      this.disableGyro();
      return false;
    }
    return this.enableGyro();
  }

  async _requestSensorPermission(Ctor) {
    if (typeof Ctor === "undefined" || typeof Ctor.requestPermission !== "function") {
      return "skip";
    }
    try {
      const state = await Ctor.requestPermission();
      return state === "granted" ? "granted" : state === "denied" ? "denied" : String(state || "denied");
    } catch {
      return "throw";
    }
  }

  /** @returns {Promise<boolean>} */
  async enableGyro() {
    if (!this.touchWalk) return false;
    const motion = await this._requestSensorPermission(
      typeof DeviceMotionEvent !== "undefined" ? DeviceMotionEvent : undefined,
    );
    const orientation = await this._requestSensorPermission(
      typeof DeviceOrientationEvent !== "undefined" ? DeviceOrientationEvent : undefined,
    );
    const granted = motion === "granted" || orientation === "granted";
    const skipped = motion === "skip" && orientation === "skip";
    const iframeThrow = motion === "throw" || orientation === "throw";
    const blocked =
      !granted &&
      !skipped &&
      !iframeThrow &&
      (motion === "denied" || orientation === "denied");
    if (blocked) {
      this.app.fire("fp:gyro", { enabled: false, denied: true });
      return false;
    }
    if (this.gyroEnabled) return true;
    this.gyroEnabled = true;
    this._gyroLast = null;
    window.addEventListener("deviceorientation", this._onDeviceOrientation, true);
    this.app.fire("fp:gyro", { enabled: true });
    return true;
  }

  disableGyro() {
    if (!this.gyroEnabled) return;
    this.gyroEnabled = false;
    this._gyroLast = null;
    this._gyroPendingYaw = 0;
    this._gyroPendingPitch = 0;
    window.removeEventListener("deviceorientation", this._onDeviceOrientation, true);
    this.app.fire("fp:gyro", { enabled: false });
  }

  _handleGyro(e) {
    if (!this.enabled || !this.gyroEnabled) return;
    // Finger look takes priority while a right-hand drag is active.
    if (this._lookTouch.id !== null) return;
    if (this.gizmo?.selectedId || this.placement?.active) return;
    if (e.alpha == null || e.beta == null || e.gamma == null) return;

    const sample = { alpha: e.alpha, beta: e.beta, gamma: e.gamma };
    if (!this._gyroLast) {
      this._gyroLast = sample;
      return;
    }

    const dAlpha = shortestDeltaDeg(this._gyroLast.alpha, sample.alpha);
    const dBeta = sample.beta - this._gyroLast.beta;
    const dGamma = sample.gamma - this._gyroLast.gamma;
    this._gyroLast = sample;

    // Map phone tilt/turn → yaw/pitch depending on portrait vs landscape.
    // Yaw: turn phone left → look left. Pitch: tilt phone up → look up.
    const angle = screenAngle();
    let dyaw = 0;
    let dpitch = 0;
    if (angle === 90) {
      dyaw = dAlpha + dBeta * 0.15;
      dpitch = dGamma;
    } else if (angle === -90 || angle === 270) {
      dyaw = dAlpha - dBeta * 0.15;
      dpitch = -dGamma;
    } else if (angle === 180) {
      dyaw = dAlpha;
      dpitch = -dBeta;
    } else {
      // Portrait (0)
      dyaw = dAlpha;
      dpitch = dBeta;
    }

    // Ignore tiny sensor noise.
    if (Math.abs(dyaw) < 0.05 && Math.abs(dpitch) < 0.05) return;

    // Accumulate until the game tick so sensor events don't stair-step the camera.
    this._gyroPendingYaw += dyaw * GYRO_YAW_SENS;
    this._gyroPendingPitch += dpitch * GYRO_PITCH_SENS;
  }

  _consumeGyro() {
    if (!this.gyroEnabled) return;
    if (!this._gyroPendingYaw && !this._gyroPendingPitch) return;
    this.yaw += this._gyroPendingYaw;
    this.pitch = pc.math.clamp(this.pitch + this._gyroPendingPitch, -PITCH_LIMIT, PITCH_LIMIT);
    this._gyroPendingYaw = 0;
    this._gyroPendingPitch = 0;
  }

  /** Suspend/resume first-person walk (used by the overhead scene-view camera). */
  setEnabled(v) {
    this.enabled = Boolean(v);
    if (!this.enabled) {
      this.releaseLock();
      if (this._joy.id !== null) {
        this._joy.id = null;
        this._joy.dx = 0;
        this._joy.dy = 0;
        this._fireJoystick();
      }
      this._clearLookStick();
      this._gyroLast = null;
      this._gyroPendingYaw = 0;
      this._gyroPendingPitch = 0;
      this._resetWalkFeel();
    }
    this.app.fire("fp:enabled", { enabled: this.enabled });
  }

  /** Eye height for the active storey (no bob). */
  _eyeY() {
    return (this.scene.room?.floorY ?? 0) + EYE_HEIGHT;
  }

  _resetWalkFeel() {
    this._velF = 0;
    this._velS = 0;
    this._bobPhase = 0;
    this._bobAmt = 0;
    this._bobY = 0;
    this._bobSway = 0;
    this._bobRoll = 0;
    this._bobPitch = 0;
  }

  /**
   * Step bob from distance walked — readable footfalls without a jog bounce.
   * @param {number} dt
   * @param {number} speedMag m/s along the floor
   * @param {number} maxSpeed reference top speed for this input mode
   */
  _updateWalkFeel(dt, speedMag, maxSpeed) {
    const ref = Math.max(0.35, maxSpeed);
    const t = pc.math.clamp(speedMag / ref, 0, 1.1);
    // Bring bob in quickly once you start moving so it never feels like a slide.
    const targetAmt = t > 0.04 ? Math.min(1, 0.35 + t * 0.65) : 0;
    this._bobAmt += (targetAmt - this._bobAmt) * Math.min(1, 7 * dt);

    if (this._bobAmt > 0.01 && speedMag > 0.02) {
      this._bobPhase += (speedMag * dt * Math.PI * 2) / BOB_STRIDE_M;
    }

    const a = this._bobAmt * (this.touchWalk ? 1.15 : 1);
    // Heel-strike dip (phase*2) + weight shift sway — classic FPS walk.
    const heel = Math.sin(this._bobPhase * 2);
    // Emphasize the downward part of each step so it reads as a footfall.
    this._bobY = (heel * 0.72 + Math.min(0, heel) * 0.28) * BOB_HEIGHT * a;
    this._bobSway = Math.sin(this._bobPhase) * BOB_SWAY * a;
    this._bobRoll = Math.sin(this._bobPhase) * BOB_ROLL * a;
    this._bobPitch = Math.sin(this._bobPhase * 2 + Math.PI * 0.45) * BOB_PITCH * a;
  }

  /** Take over the given world position + yaw (keeps the view from jumping). */
  adoptPose(position, yaw, pitch, { exact = false } = {}) {
    if (position) {
      const y = exact && Number.isFinite(position.y) ? position.y : this._eyeY();
      this.camera.setLocalPosition(position.x, y, position.z);
    }
    if (Number.isFinite(yaw)) this.yaw = yaw;
    if (Number.isFinite(pitch)) this.pitch = pc.math.clamp(pitch, -PITCH_LIMIT, PITCH_LIMIT);
    this._resetWalkFeel();
    this._applyRotation();
  }

  /** Exit pointer-lock walk mode (same effect as pressing Esc). */
  releaseLock() {
    if (this.canvas && document.pointerLockElement === this.canvas) {
      document.exitPointerLock?.();
    }
  }

  /**
   * World-space rect of a named sub-room ({x0,z0,x1,z1}) or null.
   * room.rooms rects are plan-local; offset by bounds.min like roomAt().
   */
  _namedRoomRect(room, nameRe) {
    const rooms = room?.rooms;
    const min = room?.bounds?.min ?? [0, 0, 0];
    if (!Array.isArray(rooms)) return null;
    for (const r of rooms) {
      if (!nameRe.test(String(r.name ?? r.type ?? ""))) continue;
      const rw = Number(r.width) || 0;
      const rd = Number(r.depth) || 0;
      if (rw < 1.5 || rd < 1.5) continue;
      const x0 = min[0] + (Number(r.x) || 0);
      const z0 = min[2] + (Number(r.z) || 0);
      return { x0, z0, x1: x0 + rw, z1: z0 + rd };
    }
    return null;
  }

  /** Stand just inside the entry and look across the room (preview framing). */
  spawnInRoom(room) {
    if (!room?.bounds) return;
    const { min, max } = room.bounds;

    // Preferred start: the Bedroom — at the foot of the bed (lower-center of
    // the rect), facing the headboard wall. Falls back to the generic framing
    // for plans without a named bedroom.
    const bedroom = this._namedRoomRect(room, /bedroom/i);
    let x;
    let z;
    let tx;
    let tz;
    if (bedroom) {
      x = (bedroom.x0 + bedroom.x1) / 2;
      z = bedroom.z0 + (bedroom.z1 - bedroom.z0) * 0.4;
      tx = x; // look straight at the far (bed) half of the room
      tz = bedroom.z1 - 1.2;
    } else {
      x = (min[0] + max[0]) / 2;
      z = min[2] + 0.8;
      tx = (min[0] + max[0]) / 2;
      tz = (min[2] + max[2]) / 2;
    }

    const floorY = room.floorY ?? 0;
    const eyeY = floorY + EYE_HEIGHT;
    this.camera.setLocalPosition(x, eyeY, z);

    // Aim at the target point. Compute yaw/pitch from the direction vector
    // rather than lookAt()+Euler extraction — that round-trip can flip
    // pitch ~180° and leave the camera staring up at the ceiling/behind.
    const dx = tx - x;
    const dz = tz - z;
    const dy = floorY + EYE_HEIGHT * 0.9 - eyeY; // look very slightly downward
    const horiz = Math.hypot(dx, dz) || 1e-6;
    this.yaw = (Math.atan2(-dx, -dz) * 180) / Math.PI;
    this.pitch = pc.math.clamp((Math.atan2(dy, horiz) * 180) / Math.PI, -PITCH_LIMIT, PITCH_LIMIT);
    this._resetWalkFeel();
    this._applyRotation();
  }

  _applyRotation() {
    this.camera.setLocalEulerAngles(this.pitch + this._bobPitch, this.yaw, this._bobRoll);
  }

  _handleMouseMove(e) {
    if (!this.enabled || !this.locked) return;
    this.yaw -= e.movementX * LOOK_SENS;
    this.pitch = pc.math.clamp(this.pitch - e.movementY * LOOK_SENS, -PITCH_LIMIT, PITCH_LIMIT);
    this._applyRotation();
  }

  _moveKeys() {
    const kb = this.app.keyboard;
    if (!kb) return { fwd: false, back: false, left: false, right: false, sprint: false };
    return {
      fwd: kb.isPressed(pc.KEY_W) || kb.isPressed(pc.KEY_UP),
      back: kb.isPressed(pc.KEY_S) || kb.isPressed(pc.KEY_DOWN),
      left: kb.isPressed(pc.KEY_A) || kb.isPressed(pc.KEY_LEFT),
      right: kb.isPressed(pc.KEY_D) || kb.isPressed(pc.KEY_RIGHT),
      sprint: kb.isPressed(pc.KEY_SHIFT),
    };
  }

  _update(dt) {
    if (!this.enabled) return;
    this._consumeGyro();
    // Seated / laying: camera pinned to the seat, look + gyro stay live.
    if (this.seatPose) {
      this._velF = 0;
      this._velS = 0;
      this._updateWalkFeel(dt, 0, 1);
      this.camera.setLocalPosition(this.seatPose.x, this.seatPose.y, this.seatPose.z);
      this._applyLookStick(dt);
      this._applyRotation();
      return;
    }
    // Edit mode: selected furniture — no walking; use drag / Q/E instead.
    if (this.gizmo?.selectedId || this.placement?.active) {
      this._velF = 0;
      this._velS = 0;
      this._updateWalkFeel(dt, 0, 1);
      this._applyRotation();
      return;
    }

    // Look stick turns every frame while held off-center (same feel as move stick).
    this._applyLookStick(dt);

    const joyMag = Math.hypot(this._joy.dx, this._joy.dy);
    const joyActive = this._joy.id !== null && joyMag >= TOUCH_JOY_DEADZONE;

    // Movement intent: forward (+) / strafe-right (+), each -1..1.
    let wantF = 0;
    let wantS = 0;
    let sprint = false;
    if (this.locked) {
      const keys = this._moveKeys();
      wantF = (keys.fwd ? 1 : 0) - (keys.back ? 1 : 0);
      wantS = (keys.right ? 1 : 0) - (keys.left ? 1 : 0);
      sprint = keys.sprint;
    }
    if (joyActive) {
      wantF += -this._joy.dy / TOUCH_JOY_RADIUS; // screen-up = forward
      wantS += this._joy.dx / TOUCH_JOY_RADIUS;
    }
    const wantMag = Math.hypot(wantF, wantS);
    if (wantMag > 1) {
      wantF /= wantMag;
      wantS /= wantMag;
    }

    const maxSpeed =
      (joyActive || (this.touchWalk && !this.locked) ? TOUCH_MOVE_SPEED : MOVE_SPEED) *
      (sprint ? SPRINT_MULT : 1);
    const accel = this.touchWalk ? WALK_ACCEL_TOUCH : WALK_ACCEL;
    const blend = Math.min(1, accel * dt);
    this._velF += (wantF * maxSpeed - this._velF) * blend;
    this._velS += (wantS * maxSpeed - this._velS) * blend;

    // Kill tiny residual so bob settles cleanly when stopped.
    if (Math.hypot(this._velF, this._velS) < 0.015 && wantMag < 1e-3) {
      this._velF = 0;
      this._velS = 0;
    }

    const speedMag = Math.hypot(this._velF, this._velS);
    const yawRad = (this.yaw * Math.PI) / 180;
    const fx = -Math.sin(yawRad);
    const fz = -Math.cos(yawRad);
    const rx = Math.cos(yawRad);
    const rz = -Math.sin(yawRad);

    const pos = this.camera.getLocalPosition();
    // Strip previous sway from XZ so collision stays on the true feet path.
    let baseX = pos.x - rx * this._bobSway;
    let baseZ = pos.z - rz * this._bobSway;

    if (speedMag > 1e-4) {
      const dx = (fx * this._velF + rx * this._velS) * dt;
      const dz = (fz * this._velF + rz * this._velS) * dt;
      let nx = baseX + dx;
      let nz = baseZ + dz;

      const collision = this.scene.wallCollision;
      if (collision?.segments?.length || collision?.obstacles?.length) {
        const resolved = collision.resolve(baseX, baseZ, nx, nz, PLAYER_RADIUS);
        nx = resolved.x;
        nz = resolved.z;
      } else {
        const bounds = this.scene.room?.bounds;
        if (bounds) {
          nx = pc.math.clamp(nx, bounds.min[0] + PLAYER_RADIUS, bounds.max[0] - PLAYER_RADIUS);
          nz = pc.math.clamp(nz, bounds.min[2] + PLAYER_RADIUS, bounds.max[2] - PLAYER_RADIUS);
        }
      }
      baseX = nx;
      baseZ = nz;
    }

    this._updateWalkFeel(dt, speedMag, maxSpeed);

    const eyeY = this._eyeY() + this._bobY;
    const sx = baseX + rx * this._bobSway;
    const sz = baseZ + rz * this._bobSway;
    this.camera.setLocalPosition(sx, eyeY, sz);
    this._applyRotation();
  }

  destroy() {
    if (this.canvas) {
      this.canvas.removeEventListener("click", this._onCanvasClick);
      this.canvas.removeEventListener("contextmenu", this._onContextMenu);
    }
    document.removeEventListener("pointerlockchange", this._onPointerLockChange);
    document.removeEventListener("mousemove", this._onMouseMove);
    if (this._onNativeStart) {
      const opts = { capture: true };
      document.removeEventListener("touchstart", this._onNativeStart, opts);
      document.removeEventListener("touchmove", this._onNativeMove, opts);
      document.removeEventListener("touchend", this._onNativeEnd, opts);
      document.removeEventListener("touchcancel", this._onNativeEnd, opts);
    }
    const touch = this.app.touch;
    if (touch) {
      touch.off(pc.EVENT_TOUCHSTART, this._onTouchStart, this);
      touch.off(pc.EVENT_TOUCHMOVE, this._onTouchMove, this);
      touch.off(pc.EVENT_TOUCHEND, this._onTouchEnd, this);
      touch.off(pc.EVENT_TOUCHCANCEL, this._onTouchEnd, this);
    }
    if (document.pointerLockElement === this.canvas) document.exitPointerLock?.();
    this.disableGyro();
  }
}

export default FirstPersonControls;
